import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const GeneralSettings = () => {
  const [projectName, setProjectName] = useState('E-commerce Store');
  const [description, setDescription] = useState('A modern online store for selling products');
  const [seoTitle, setSeoTitle] = useState('E-commerce Store - Shop Online');
  const [metaDescription, setMetaDescription] = useState('Discover amazing products at great prices. Shop now for the best deals on electronics, fashion, and more.');
  const [favicon, setFavicon] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const handleFaviconUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      setFavicon(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreview(reader?.result);
      };
      reader?.readAsDataURL(file);
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    setHasChanges(false);
  };

  const handleInputChange = (setter) => (e) => {
    setter(e?.target?.value);
    setHasChanges(true);
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          Project Information
        </h3>
        
        <div className="space-y-4 md:space-y-5">
          <Input
            label="Project Name"
            type="text"
            placeholder="Enter project name"
            value={projectName}
            onChange={handleInputChange(setProjectName)}
            required
            description="This name will be displayed in your dashboard"
          />

          <div className="space-y-2">
            <label className="caption font-medium text-foreground">
              Description
            </label>
            <textarea
              value={description}
              onChange={handleInputChange(setDescription)}
              placeholder="Enter project description"
              rows={4}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg caption text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 resize-none"
            />
            <p className="text-xs text-muted-foreground">
              Provide a brief description of your project
            </p>
          </div>

          <div className="space-y-2">
            <label className="caption font-medium text-foreground">
              Favicon
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-16 h-16 bg-muted rounded-lg border-2 border-dashed border-border flex items-center justify-center flex-shrink-0">
                {faviconPreview ? (
                  <Image
                    src={faviconPreview}
                    alt="Favicon preview showing uploaded project icon"
                    className="w-12 h-12 object-contain"
                  />
                ) : (
                  <Icon name="Image" size={24} className="text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input
                  type="file"
                  accept="image/png,image/x-icon,image/svg+xml"
                  onChange={handleFaviconUpload}
                  className="hidden"
                  id="favicon-upload"
                />
                <label htmlFor="favicon-upload">
                  <Button variant="outline" size="sm" asChild>
                    <span className="cursor-pointer">
                      <Icon name="Upload" size={16} className="mr-2" />
                      Upload Favicon
                    </span>
                  </Button>
                </label>
                <p className="text-xs text-muted-foreground">
                  Recommended: 32x32px PNG, ICO, or SVG format
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-4 md:mb-6">
          SEO Settings
        </h3>
        
        <div className="space-y-4 md:space-y-5">
          <div className="space-y-2">
            <Input
              label="SEO Title"
              type="text"
              placeholder="Enter SEO title"
              value={seoTitle}
              onChange={handleInputChange(setSeoTitle)}
              required
              description={`${seoTitle?.length}/60 characters`}
            />
            {seoTitle?.length > 60 && (
              <p className="text-xs text-warning flex items-center gap-1">
                <Icon name="AlertCircle" size={14} />
                Title exceeds recommended length
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="caption font-medium text-foreground">
              Meta Description
            </label>
            <textarea
              value={metaDescription}
              onChange={handleInputChange(setMetaDescription)}
              placeholder="Enter meta description"
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border rounded-lg caption text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-250 resize-none"
            />
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Appears in search engine results
              </p>
              <p className={`text-xs ${metaDescription?.length > 160 ? 'text-warning' : 'text-muted-foreground'}`}>
                {metaDescription?.length}/160 characters
              </p>
            </div>
          </div>

          <div className="bg-muted rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-primary mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="caption font-medium text-foreground">SEO Best Practices</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Keep title under 60 characters</li>
                  <li>• Keep meta description under 160 characters</li>
                  <li>• Include relevant keywords naturally</li>
                  <li>• Make descriptions compelling and accurate</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      {hasChanges && (
        <div className="sticky bottom-4 bg-card border border-border rounded-lg p-4 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Icon name="AlertCircle" size={20} className="text-warning" />
              <p className="caption text-foreground">You have unsaved changes</p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHasChanges(false)}
                className="flex-1 sm:flex-none"
              >
                Discard
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                iconName="Check"
                iconPosition="left"
                className="flex-1 sm:flex-none"
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralSettings;